import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Loader2, AlertCircle, BedDouble, TrendingUp } from 'lucide-react';

/**
 * StaysIntelTile — Stays By MindEscapes (Ketti)
 * --------------------------------------------------
 * Wires `/api/stays/snapshot` and `/api/stays/inhouse` for the ME
 * division page.
 *
 * RULE 16 lives here on the UI side:
 *   - HEADLINE: stays_revenue = Pure Room Revenue + non-F&B extras
 *   - F&B charged to room is shown as a small caveat line, NOT added
 *     to the headline (already counted in Celeste POS)
 *
 * Palette: cream/navy canonical (matches MonthlyArchive 5 May 2026 fix).
 */
const fmtINR = (v) => {
  if (v == null || isNaN(v)) return '—';
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${Math.round(v)}`;
};

export default function StaysIntelTile() {
  const [snap, setSnap] = useState(null);
  const [range, setRange] = useState('mtd');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/stays/snapshot?range=${range}`)
      .then((r) => !cancel && setSnap(r.data))
      .catch((e) => !cancel && setError(e.message || 'Failed to load Stays'))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [range]);

  if (loading) {
    return (
      <section
        data-testid="stays-intel-tile"
        className="mt-12 p-8 rounded-xl bg-white border"
        style={{ borderColor: '#E5E1DC' }}
      >
        <div className="flex items-center gap-3 text-sm" style={{ color: '#6B7280' }}>
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading Stays Intelligence…
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section
        data-testid="stays-intel-tile"
        className="mt-12 p-8 rounded-xl border"
        style={{ backgroundColor: '#FFF5F4', borderColor: '#D4726A40' }}
      >
        <div className="flex items-center gap-3 text-sm" style={{ color: '#BA3F3F' }}>
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      </section>
    );
  }

  const occupancyColor =
    snap.occupancy_now_pct >= 70 ? '#059669'
    : snap.occupancy_now_pct >= 40 ? '#D97706' : '#BA3F3F';

  return (
    <section data-testid="stays-intel-tile" className="mt-12">
      <header className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BedDouble size={20} style={{ color: '#1B2A4A' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#1B2A4A' }}>
            Stays Intelligence
            <span className="ml-3 text-xs font-normal" style={{ color: '#9CA3AF' }}>
              {snap.outlet_name} · {snap.from_date} → {snap.to_date}
            </span>
          </h3>
        </div>
        <RangePicker value={range} onChange={setRange} />
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Headline + occupancy */}
        <div
          data-testid="stays-headline"
          className="p-5 rounded-xl bg-white border"
          style={{ borderColor: '#E5E1DC' }}
        >
          <div className="text-xs uppercase tracking-wider" style={{ color: '#9CA3AF' }}>
            Stays Revenue
            <span
              className="ml-1.5 text-[9px]"
              title="Pure Room Revenue + non-F&B extras (Rule 16)"
              style={{ color: '#9CA3AF' }}
            >
              (Rule 16)
            </span>
          </div>
          <div className="text-3xl font-bold mt-1" style={{ color: '#1B2A4A' }}>
            {fmtINR(snap.stays_revenue)}
          </div>
          <div className="text-xs mt-1" style={{ color: '#6B7280' }}>
            {snap.folio_count} folios · ADR {fmtINR(snap.adr)} · RevPAR {fmtINR(snap.revpar)}
          </div>

          <div className="mt-4 pt-4 border-t" style={{ borderColor: '#F0EDE8' }}>
            <div className="text-xs uppercase tracking-wider" style={{ color: '#9CA3AF' }}>
              Live occupancy
            </div>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="text-2xl font-bold" style={{ color: occupancyColor }}>
                {snap.occupied_now}/{snap.total_rooms}
              </span>
              <span className="text-sm" style={{ color: occupancyColor }}>
                {snap.occupancy_now_pct}%
              </span>
            </div>
          </div>
        </div>

        {/* Decomposition */}
        <div
          data-testid="stays-decomposition"
          className="p-5 rounded-xl bg-white border"
          style={{ borderColor: '#E5E1DC' }}
        >
          <div className="text-xs uppercase tracking-wider mb-3" style={{ color: '#9CA3AF' }}>
            Decomposition
          </div>
          <ul className="space-y-2 text-sm">
            <li className="flex justify-between">
              <span style={{ color: '#6B7280' }}>Pure room revenue</span>
              <span className="font-mono" style={{ color: '#1B2A4A' }}>
                {fmtINR(snap.pure_room_revenue)}
              </span>
            </li>
            <li className="flex justify-between">
              <span style={{ color: '#6B7280' }}>Non-F&amp;B extras</span>
              <span className="font-mono" style={{ color: '#1B2A4A' }}>
                {fmtINR(snap.other_extras)}
              </span>
            </li>
            <li
              className="flex justify-between border-t pt-2 mt-2"
              style={{ borderColor: '#E5E1DC' }}
            >
              <span className="font-medium" style={{ color: '#1B2A4A' }}>
                Stays revenue
              </span>
              <span className="font-mono font-semibold" style={{ color: '#059669' }}>
                {fmtINR(snap.stays_revenue)}
              </span>
            </li>
            <li
              className="flex justify-between text-xs italic mt-3 pt-3 border-t"
              style={{ borderColor: '#F0EDE8', color: '#9CA3AF' }}
              title="F&B charged to room — NOT added to Stays headline because it's already in Celeste POS"
            >
              <span>F&amp;B charged to room (excluded · Rule 16)</span>
              <span className="font-mono">{fmtINR(snap.fnb_charged_to_room)}</span>
            </li>
          </ul>
        </div>

        {/* Channel mix */}
        <div
          data-testid="stays-channels"
          className="p-5 rounded-xl bg-white border"
          style={{ borderColor: '#E5E1DC' }}
        >
          <div className="text-xs uppercase tracking-wider mb-3" style={{ color: '#9CA3AF' }}>
            Channel Mix
          </div>
          {snap.channel_mix.length === 0 ? (
            <div className="text-sm italic" style={{ color: '#9CA3AF' }}>
              No bookings in this range
            </div>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {snap.channel_mix.slice(0, 7).map((ch) => {
                const share = snap.stays_revenue > 0
                  ? (ch.revenue / snap.stays_revenue) * 100 : 0;
                return (
                  <li key={ch.channel} className="flex justify-between gap-3">
                    <span className="truncate" style={{ color: '#1B2A4A' }}>
                      {ch.channel || 'Direct'}
                    </span>
                    <span className="font-mono text-xs whitespace-nowrap" style={{ color: '#6B7280' }}>
                      {ch.folios} · {share.toFixed(0)}%
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <p className="md:col-span-3 text-[11px] italic mt-3" style={{ color: '#9CA3AF' }}>
        Source: eZee Absolute PMS · synced nightly @ 23:50 IST · Pure Room Revenue
        per Rule 16 — F&amp;B charged to room is counted in Celeste POS, not here.
      </p>
    </section>
  );
}

function RangePicker({ value, onChange }) {
  const ranges = [
    { key: 'today', label: 'Today' },
    { key: 'mtd', label: 'MTD' },
    { key: 'last_30d', label: 'Last 30d' },
    { key: 'fy_to_date', label: 'FY' },
  ];
  return (
    <div className="flex gap-1.5" data-testid="stays-range-picker">
      {ranges.map((r) => (
        <button
          key={r.key}
          data-testid={`stays-range-${r.key}`}
          onClick={() => onChange(r.key)}
          className="px-3 py-1.5 text-xs rounded-md border transition-colors"
          style={
            value === r.key
              ? { backgroundColor: '#1B2A4A', color: '#FFF', borderColor: '#1B2A4A' }
              : { backgroundColor: '#FFF', color: '#1B2A4A', borderColor: '#E5E1DC' }
          }
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}
